import { useCallback, useEffect, useRef } from "react";
import { analytics } from "./analytics";

const SNAPSHOT_MS = 8000;
const TYPING_FLUSH_MS = 5000;
const SNAPSHOT_MAX = 2000;

const keyClass = (e) => {
  if (e.key === "Backspace" || e.key === "Delete") return "delete";
  if (e.key === "Enter") return "enter";
  if (e.key === " ") return "space";
  if (e.key.length === 1) return e.metaKey || e.ctrlKey ? "shortcut" : "char";
  if (/^Arrow|^Home$|^End$|^Page/.test(e.key)) return "nav";
  if (/^(Shift|Control|Alt|Meta|CapsLock)$/.test(e.key)) return "modifier";
  return "other";
};

export function useComposerTelemetry({ conversationId, parentId, editing }) {
  const s = useRef(null);
  const timers = useRef({});
  const base = useCallback((extra = {}) => ({ category: "composer", conversation_id: conversationId, metadata: { thread: !!parentId, editing: !!editing, ...extra } }), [conversationId, parentId, editing]);

  const ensure = () => {
    if (!s.current) {
      const t = Date.now();
      s.current = { started: t, keystrokes: 0, chars_added: 0, chars_deleted: 0, backspaces: 0, pastes: 0, enters: 0, lastLen: 0, lastText: "", flushed: { keystrokes: 0, chars_added: 0, chars_deleted: 0, backspaces: 0 }, flushedAt: t, snapshotLen: -1 };
      analytics.track("composer_started", base());
    }
    return s.current;
  };

  const flushTyping = useCallback((reason) => {
    const c = s.current;
    if (!c || c.keystrokes === c.flushed.keystrokes) return;
    const now = Date.now();
    analytics.track("composer_typing", { ...base({
      keystrokes: c.keystrokes - c.flushed.keystrokes, chars_added: c.chars_added - c.flushed.chars_added,
      chars_deleted: c.chars_deleted - c.flushed.chars_deleted, backspaces: c.backspaces - c.flushed.backspaces,
      duration_ms: now - c.flushedAt, draft_length: c.lastLen, reason,
    }), label: reason });
    c.flushed = { keystrokes: c.keystrokes, chars_added: c.chars_added, chars_deleted: c.chars_deleted, backspaces: c.backspaces };
    c.flushedAt = now;
  }, [base]);

  const snapshot = useCallback((reason) => {
    const c = s.current;
    if (!c || !analytics.on("snapshots") || !c.lastText.trim() || c.snapshotLen === c.lastText.length) return;
    c.snapshotLen = c.lastText.length;
    analytics.track("draft_snapshot", { ...base({ text: c.lastText.slice(0, SNAPSHOT_MAX), length: c.lastText.length, reason }), label: reason });
  }, [base]);

  const schedule = () => {
    clearTimeout(timers.current.typing);
    timers.current.typing = setTimeout(() => flushTyping("pause"), TYPING_FLUSH_MS);
    if (!timers.current.snap) timers.current.snap = setTimeout(() => { timers.current.snap = null; snapshot("interval"); }, SNAPSHOT_MS);
  };

  const onKeyDown = (e) => {
    const c = ensure();
    const cls = keyClass(e);
    if (cls === "modifier") return;
    c.keystrokes++;
    if (cls === "delete") c.backspaces++;
    if (cls === "enter") c.enters++;
    if (analytics.on("keystrokes")) {
      analytics.track("keystroke", { ...base({ key: cls === "char" || cls === "space" ? e.key : e.key, code: e.code, class: cls, shift: e.shiftKey, meta: e.metaKey || e.ctrlKey, caret: e.target.selectionStart ?? null }), label: cls });
    }
    schedule();
  };

  const onInput = (prev, next) => {
    const c = ensure();
    const d = next.length - prev.length;
    if (d > 0) c.chars_added += d; else c.chars_deleted += -d;
    c.lastLen = next.length;
    c.lastText = next;
    schedule();
  };

  const onPaste = (len) => { const c = ensure(); c.pastes++; c.chars_added += len; analytics.track("composer_paste", { ...base({ length: len }) }); };

  const finish = () => { clearTimeout(timers.current.typing); clearTimeout(timers.current.snap); timers.current.snap = null; s.current = null; };

  const onSend = (text, files) => {
    const c = s.current;
    flushTyping("send");
    const t = Date.now();
    analytics.track("message_composed", { ...base({
      duration_ms: c ? t - c.started : 0, keystrokes: c?.keystrokes || 0, chars_added: c?.chars_added || 0, chars_deleted: c?.chars_deleted || 0,
      backspaces: c?.backspaces || 0, pastes: c?.pastes || 0, length: text.length, words: text.trim() ? text.trim().split(/\s+/).length : 0, files,
      efficiency: c && c.chars_added ? Math.round((text.length / c.chars_added) * 100) / 100 : null,
    }) });
    finish();
  };

  const onAbandon = useCallback((text, reason) => {
    const c = s.current;
    if (!c) return;
    flushTyping(reason);
    if (text && text.trim()) {
      analytics.track("draft_abandoned", { ...base({ length: text.length, keystrokes: c.keystrokes, duration_ms: Date.now() - c.started, reason, text: analytics.on("snapshots") ? text.slice(0, SNAPSHOT_MAX) : undefined }), label: reason });
    }
    finish();
  }, [base, flushTyping]);

  const onBlur = () => { flushTyping("blur"); snapshot("blur"); };

  return { onKeyDown, onInput, onPaste, onSend, onAbandon, onBlur };
}

export function useReadingTelemetry(conversationId, scrollRef, messageCount) {
  const seen = useRef(new Set());
  const pendingSeen = useRef([]);
  const dwell = useRef(null);
  const observed = useRef(new WeakSet());
  const observer = useRef(null);
  const flushTimer = useRef(null);

  const flushSeen = useCallback(() => {
    clearTimeout(flushTimer.current);
    flushTimer.current = null;
    const ids = pendingSeen.current.splice(0);
    if (!ids.length || !analytics.on("reading")) return;
    analytics.track("messages_seen", { category: "reading", conversation_id: conversationId, metadata: { count: ids.length, message_ids: ids.slice(0, 50) } });
  }, [conversationId]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !analytics.on("reading")) return undefined;
    seen.current = new Set();
    pendingSeen.current = [];
    observed.current = new WeakSet();
    dwell.current = { started: Date.now(), visibleSince: document.visibilityState === "visible" ? Date.now() : null, visibleMs: 0, maxUp: 0, pages: 0 };
    observer.current = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (!en.isIntersecting) return;
        const id = en.target.id?.replace(/^msg-/, "");
        if (!id || seen.current.has(id)) return;
        seen.current.add(id);
        pendingSeen.current.push(id);
      });
      if (pendingSeen.current.length && !flushTimer.current) flushTimer.current = setTimeout(flushSeen, 3000);
    }, { root: el, threshold: 0.5 });
    const onScroll = () => {
      const d = dwell.current;
      if (!d) return;
      const up = el.scrollHeight - el.clientHeight > 0 ? 1 - el.scrollTop / (el.scrollHeight - el.clientHeight) : 0;
      d.maxUp = Math.max(d.maxUp, Math.min(1, Math.max(0, up)));
    };
    const onVis = () => {
      const d = dwell.current;
      if (!d) return;
      if (document.visibilityState === "hidden") { if (d.visibleSince) d.visibleMs += Date.now() - d.visibleSince; d.visibleSince = null; flushSeen(); }
      else d.visibleSince = Date.now();
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("visibilitychange", onVis);
    return () => {
      el.removeEventListener("scroll", onScroll);
      document.removeEventListener("visibilitychange", onVis);
      observer.current?.disconnect();
      flushSeen();
      const d = dwell.current;
      if (d) {
        if (d.visibleSince) d.visibleMs += Date.now() - d.visibleSince;
        analytics.track("conversation_dwell", { category: "reading", conversation_id: conversationId, metadata: {
          duration_ms: d.visibleMs, wall_ms: Date.now() - d.started, messages_seen: seen.current.size, max_scroll_up: Math.round(d.maxUp * 100) / 100,
        } });
      }
      dwell.current = null;
    };
  }, [conversationId, scrollRef, flushSeen]);

  useEffect(() => {
    const el = scrollRef.current;
    const ob = observer.current;
    if (!el || !ob) return;
    el.querySelectorAll(".tc-msg[id^='msg-']").forEach((node) => { if (!observed.current.has(node)) { observed.current.add(node); ob.observe(node); } });
  }, [messageCount, conversationId, scrollRef]);
}
